// Картата на един ред от „Монитори" на телефон.
//
// Общата транспозиция „ред на колона" би направила от 14 колони карта от 14
// реда — на екрана се събира един запис. Тук горе стои това, което човек
// търси (коя маса, кой артикул), а четирите фази са в решетка 2×2: време и
// под него кой го е направил, точно както изглеждаше старата клетка.

import { useTranslation } from "react-i18next";

import { formatNumber, parseDecimal } from "../../../../components/dataTable";
import type { KitchenMonitorReportRow } from "../../../../api/reportsKitchenMonitors";
import { formatDuration, formatMoment } from "./kitchenMonitorsFormat";

type Props = {
  row: KitchenMonitorReportRow;
  /** i18n ключът на „Добавка". */
  addonKey: string;
  /** i18n ключът на продаваемото — „Асортимент" или „Рецепта" (склад). */
  sellableKey: string;
};

export const KitchenMonitorMobileCard = ({
  row,
  addonKey,
  sellableKey,
}: Props) => {
  const { t } = useTranslation();

  const phases = [
    {
      id: "to_start",
      label: t("reportsPage.kmStatusInProgress"),
      seconds: row.seconds_to_start,
      operator: row.started_by,
    },
    {
      id: "cooking",
      label: t("reportsPage.kmStatusCooked"),
      seconds: row.seconds_cooking,
      operator: row.cooked_by,
    },
    {
      id: "to_issue",
      label: t("reportsPage.kmStatusIssued"),
      seconds: row.seconds_to_issue,
      operator: row.issued_by,
    },
    {
      id: "to_serve",
      label: t("reportsPage.kmStatusServed"),
      seconds: row.seconds_to_serve,
      operator: row.served_by,
    },
  ];

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-[15px] font-bold text-ink">
          {row.bill_name}
        </span>
        <span className="font-pos-mono text-[13px] font-bold tabular-nums text-ink">
          {`× ${formatNumber(parseDecimal(row.quantity))}`}
        </span>
      </div>

      <div className="flex items-start gap-2">
        <span className="min-w-0 flex-1 text-[13px] font-semibold leading-[1.25] text-ink">
          {row.item_name}
        </span>
        <span
          className={`shrink-0 rounded-md px-2 py-[3px] text-[11px] font-bold leading-[normal] ${
            row.is_addition
              ? "bg-fill text-body"
              : "bg-brand-tint text-brand-deep-ink"
          }`}
        >
          {t(row.is_addition ? addonKey : sellableKey)}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] font-medium text-muted">
        <span className="truncate">{row.station_name}</span>
        <span aria-hidden>·</span>
        <span className="font-pos-mono tabular-nums">
          {formatMoment(row.ordered_at)}
        </span>
      </div>

      <dl className="m-0 grid grid-cols-2 gap-x-3 gap-y-1.5 border-t border-shade-1 pt-1.5">
        {phases.map((phase) => (
          <div key={phase.id} className="flex min-w-0 flex-col gap-0.5">
            <dt className="font-pos-mono text-[10px] font-bold uppercase leading-none tracking-[0.08em] text-muted">
              {phase.label}
            </dt>
            <dd className="m-0 flex min-w-0 flex-col leading-[1.25]">
              <span
                className={`font-pos-mono text-[12.5px] font-bold tabular-nums ${
                  phase.seconds === null ? "text-faint" : "text-ink"
                }`}
              >
                {formatDuration(phase.seconds)}
              </span>
              {phase.operator ? (
                <span className="min-w-0 truncate text-[11px] font-medium text-soft">
                  {phase.operator}
                </span>
              ) : null}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};
